import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryGroup,
  VictoryTheme,
} from "victory";
import { CommercialUnitDataHour } from "../../types/commercial";
import Box from "../Box";
import useBreakPoint from "../../utils/useBreakPoint";
import { useStores } from "../../context/storesContext";
import { parseStoreHours } from "../../utils/storeUtils";
import { useMemo } from "react";

type CAByHourPieChartProps = {
  byHourToday: Array<CommercialUnitDataHour>;
  byHourLastYear: Array<CommercialUnitDataHour>;
};
const CAByHourPieChart = ({
  byHourToday,
  byHourLastYear,
}: CAByHourPieChartProps) => {
  const isBreakpoint = useBreakPoint();
  const { selectedStore } = useStores();

  const { openingHour, closingHour } = parseStoreHours(selectedStore);

const { 
  values, 
  formatValues, 
  filteredByHourToday, 
  filteredByHourLastYear 
} = useMemo(() => {
  const values = [];
  const formatValues = [];

  for (let hour = openingHour + 1; hour <= closingHour; hour++) {
    values.push(hour - openingHour + 1);
    formatValues.push(`${hour}h`);
  }

  const isWithinHours = (h: CommercialUnitDataHour) => h?.time > openingHour && h?.time <= closingHour;

  return {
    values,
    formatValues,
    filteredByHourToday: byHourToday?.filter(isWithinHours) ?? [],
    filteredByHourLastYear: byHourLastYear?.filter(isWithinHours) ?? [],
  };
}, [openingHour, closingHour, byHourToday, byHourLastYear]);

  const lastHourInDataset = Math.min(byHourLastYear.at(-1)?.time ?? closingHour, closingHour);
  const dividante = (closingHour + 1) - lastHourInDataset;
  const maxturnover =
    byHourLastYear.length && byHourToday.length
      ? Math.max(
          byHourToday[byHourToday.length - 1].turnover,
          byHourLastYear[byHourLastYear.length - 1].turnover
        ) *
        dividante *
        1000
      : byHourLastYear.length
      ? byHourLastYear[byHourLastYear.length - 1].turnover * dividante * 1000
      : 0;
  let step = Number((maxturnover / 5.7)?.toFixed(0));
  const marge = step < 1000 ? (step < 100 ? 10 : 100) : 1000;
  step = (step / marge) * marge;

  const echaloneValueMax = () => {
    let val = 0;
    const tab = [] as number[];
    tab.push(0);
    for (let index = 1; index < 7; index++) {
      val += step;
      tab.push(Math.abs(Number(val?.toFixed(0))));
    }
    return tab;
  };

  const formatAxisValue = (value: number) => {
    const absValue = Math.abs(value);
    if (absValue >= 1_000_000) {
      return `${Math.round(value / 1_000_000)}M€`;
    }
    if (absValue >= 1000) {
      return `${Math.round(value / 1000)}K€`;
    }
    return `${Math.round(value)}€`;
  };


  return (
    <Box width={"auto"} display={"flex"} alignSelf={"center"}>
      <VictoryChart
        width={
          isBreakpoint === "phone"
            ? 800
            : isBreakpoint === "tablet"
            ? 2000
            : 3100
        }
        height={isBreakpoint === "phone" ? 600 : 880}
        domainPadding={{ x: isBreakpoint === "phone" ? 35 : 40 }}
        padding={{
          left: isBreakpoint === "tablet" ? 140 : 120,
          top: 50,
          right: 30,
          bottom: 60,
        }}
        theme={VictoryTheme.grayscale}
        data-testid={"victory-chart"}
      >
        <VictoryAxis
          tickValues={values}
          tickFormat={formatValues}
          style={{
            tickLabels: {
              fontSize:
                isBreakpoint === "phone"
                  ? "24"
                  : isBreakpoint === "tablet"
                  ? "50"
                  : "33",
            },
            axis: { stroke: "transparent" },
          }}
        />

        <VictoryAxis
          dependentAxis
          tickValues={echaloneValueMax()}
          tickFormat={formatAxisValue}
          style={{
            tickLabels: {
              fontSize:
                isBreakpoint === "phone"
                  ? "24"
                  : isBreakpoint === "tablet"
                  ? "50"
                  : "33",
            },
            axis: { stroke: "transparent" },
          }}
        />
        <VictoryGroup
          offset={isBreakpoint === "phone" ? 17 : 40}
          colorScale={["#0970E6", "#00BF7D"]}
          style={{
            data: { width: isBreakpoint === "phone" ? 17 : 40, margin: "40px" },
            parent: { marginTop: "40px" },
          }}
        >
          <VictoryBar
            data={filteredByHourToday}
            x="time"
            y={(d) => d.turnover * 1000}
            cornerRadius={{ top: 5 }}
            data-testid={"victory-bar1"}
          />
          <VictoryBar
            data={filteredByHourLastYear}
            x="time"
            y={(d) => d.turnover * 1000}
            cornerRadius={{ top: 5 }}
            data-testid={"victory-bar2"}
          />
        </VictoryGroup>
      </VictoryChart>
    </Box>
  );
};

export default CAByHourPieChart;
